import Vue from 'vue';
import App from './App.vue';

import {TweenMax} from 'gsap'; // This is being used even though it doesn't look like it

import './filters';
import './directives.js';

import store from './store';
import { createPinia, PiniaVuePlugin } from 'pinia'
import router from './router';
import api from './api';
import utils from './services/utils';
import {ENUMS} from "./services/enums";
import {MODELS} from "./services/models";

import VueMeta from 'vue-meta';
import VueGtm from 'vue-gtm';
import Buefy from "buefy";
import VueCurrencyFilter from 'vue-currency-filter';
import * as Sentry from "@sentry/vue";
import VueObserveVisibility from 'vue-observe-visibility';

if (process.env.MIX_SENTRY_DSN) {
  Sentry.init({
    Vue,
    dsn: process.env.MIX_SENTRY_DSN,
    allowUrls: [
      'forefrontcrm.com',
      'flippilot.com',
      'forefront.test',
      'forefrontcrm.test',
      'flippilot.test',
      'forefront.local',
      'forefrontcrm.local',
      'flippilot.local',
      'forefront.dev',
      'forefrontcrm.dev',
      'flippilot.dev',
      'localhost'
    ],
    // Filter out errors that aren't really errors
    beforeSend: (event) => {
      // Do not send errors with the following messages
      if (event.message && event.message.match(/Cannot read properties of null/i)) {
        return null;
      }
      if (event.message && event.message.match(/Cannot read properties of undefined/i)) {
        return null;
      }
      if (event.message && event.message.match(/Avoided redundant navigation/i)) {
        return null;
      }
      if (event.message && event.message.match(/undefined is not an object/i)) {
        return null;
      }

      // Default to sending error
      return event;
    },
    /*
     * This Vue Tracing Integration allows you to track rendering performance during an initial application load
     * It's commented out because it was causing a lot of extra issues
     */
    // integrations: [
    //   new BrowserTracing({
    //     routingInstrumentation: Sentry.vueRouterInstrumentation(router),
    //     tracingOrigins: [
    //       "localhost",
    //       "flippilot.com",
    //       "forefrontcrm.com",
    //       /^\//
    //     ],
    //   }),
    // ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: process.env.MIX_SENTRY_SAMPLE_RATE,
  });

  /**
   * Commented out this user stuff because it split errors by user and caused issue
   */
  // const user = LocalStorage.getAuthUser()

  // if(user) {
  //   Sentry.setUser({
  //     email: user.email,
  //     id: user.id,
  //   });
  // }
}

let gtmEnabled = false;
if (process.env.NODE_ENV === 'production') {
  gtmEnabled = true;
}

Vue.use(VueGtm, {
  id: 'GTM-W4QLMG9',
  enabled: gtmEnabled,
  debug: true,
  loadScript: true,
  vueRouter: router,
  ignoredViews: []
});

Object.defineProperty(Vue.prototype, '$api', {value: api});
Object.defineProperty(Vue.prototype, '$utils', {value: utils});

Vue.use(VueMeta, {
  refreshOnceOnNavigation: true
});

Vue.use(Buefy, {
  defaultToastPosition: 'is-bottom',
});

Vue.use(VueObserveVisibility);

Vue.use(VueCurrencyFilter, {
  symbol: '$',
  thousandsSeparator: ',',
  fractionCount: 2,
  fractionSeparator: '.',
  symbolPosition: 'front',
  symbolSpacing: false
});

Vue.config.productionTip = false;

// Layouts
import LayoutAuth from "./layouts/auth/LayoutAuth";
import LayoutPage from "./layouts/page/LayoutPage";
import LayoutEmpty from "./layouts/page/LayoutEmptyPage";
import LayoutMobile from "./layouts/mobile/LayoutMobile";

Vue.component('LayoutAuth', LayoutAuth);
Vue.component('LayoutPage', LayoutPage);
Vue.component('LayoutEmpty', LayoutEmpty);
Vue.component('LayoutMobile', LayoutMobile);

// App Component
import AppAvatar from "./components/app/AppAvatar";
import AppDivider from "./components/app/AppDivider";
import AppEmptyMessage from "./components/app/AppEmptyMessage";
import AppHelpLink from "./components/app/AppHelpLink";
import AppInstruction from "./components/app/AppInstruction";
import AppKeyValueItem from "./components/app/AppKeyValueItem";
import AppLoadingSection from "./components/app/AppLoadingSection";
import AppRecord from "./components/app/AppRecord";
import AppStripeElement from "./components/app/AppStripeElement";

Vue.component('AppAvatar', AppAvatar);
Vue.component('AppDivider', AppDivider);
Vue.component('AppEmptyMessage', AppEmptyMessage);
Vue.component('AppHelpLink', AppHelpLink);
Vue.component('AppInstruction', AppInstruction);
Vue.component('AppKeyValueItem', AppKeyValueItem);
Vue.component('AppLoadingSection', AppLoadingSection);
Vue.component('AppRecord', AppRecord);
Vue.component('AppStripeElement', AppStripeElement);

// Badge Components
import BadgeDefault from "./components/badge/BadgeDefault";

Vue.component('BadgeDefault', BadgeDefault);

// Button Components
import ButtonDefault from "./components/button/ButtonDefault";
import ButtonIcon from "./components/button/ButtonIcon";
import ButtonLink from "./components/button/ButtonLink";
import ButtonOutlined from "./components/button/ButtonOutlined";

Vue.component('ButtonDefault', ButtonDefault);
Vue.component('ButtonIcon', ButtonIcon);
Vue.component('ButtonLink', ButtonLink);
Vue.component('ButtonOutlined', ButtonOutlined);

// Chart Components
import ChartBar from "./components/charts/ChartBar";
import ChartLine from "./components/charts/ChartLine";

Vue.component('ChartBar', ChartBar);
Vue.component('ChartLine', ChartLine);

// Filter
import FilterBar from "./components/filter/FilterBar";
import FilterBaths from "./components/filter/FilterBaths.vue";
import FilterBeds from "./components/filter/FilterBeds.vue";
import FilterItem from "./components/filter/FilterItem";
import FilterCampaigns from "./components/filter/FilterCampaigns";
import FilterCampaignsMultiple from "./components/filter/FilterCampaignsMultiple";
import FilterExistStrategies from "./components/filter/FilterExistStrategies";
import FilterLeadDeal from "./components/filter/FilterLeadDeal";
import FilterLotSize from "./components/filter/FilterLotSize.vue";
import FilterPhoneNumbersMultiple from "./components/filter/FilterPhoneNumbersMultiple"
import FilterPostcode from "./components/filter/FilterPostcode.vue"
import FilterPipelineState from "./components/filter/FilterPipelineState"
import FilterSequences from "./components/filter/FilterSequences";
import FilterSequenceStatuses from "./components/filter/FilterSequenceStatuses";
import FilterSources from "./components/filter/FilterSources";
import FilterSourcesSelector from "./components/filter/FilterSourcesSelector";
import FilterStatuses from "./components/filter/FilterStatuses";
import FilterStatusesMultiple from "./components/filter/FilterStatusesMultiple.vue";
import FilterSqft from "./components/filter/FilterSqft.vue";
import FilterTemperatures from "./components/filter/FilterTemperatures";
import FilterDealProbabilityScore from "./components/filter/FilterDealProbabilityScore";
import FilterTimeFrames from "./components/filter/FilterTimeFrames";
import FilterUsers from "./components/filter/FilterUsers";
import FilterYearBuilt from "./components/filter/FilterYearBuilt.vue";
import FilterZipcode from "./components/filter/FilterPostcode.vue";

import FilterButtonClear from "./components/filter/old/FilterButtonClear";
import FilterButtonReload from "./components/filter/old/FilterButtonReload";
import FilterButtonAdd from "./components/filter/old/FilterButtonAdd";
import FilterFieldDate from "./components/filter/old/FilterFieldDate";
import FilterFieldDefault from "./components/filter/old/FilterFieldDefault";
import FilterFieldDropdown from "./components/filter/old/FilterFieldDropdown";
import FilterFieldUsers from "./components/filter/old/FilterFieldUsers";
import FilterLink from "./components/filter/old/FilterLink";

Vue.component('FilterBar', FilterBar);
Vue.component('FilterBaths', FilterBaths);
Vue.component('FilterBeds', FilterBeds);
Vue.component('FilterItem', FilterItem);
Vue.component('FilterCampaigns', FilterCampaigns);
Vue.component('FilterCampaignsMultiple', FilterCampaignsMultiple);
Vue.component('FilterExistStrategies', FilterExistStrategies);
Vue.component('FilterLeadDeal', FilterLeadDeal);
Vue.component('FilterLotSize', FilterLotSize);
Vue.component('FilterPhoneNumbersMultiple', FilterPhoneNumbersMultiple);
Vue.component('FilterPipelineState', FilterPipelineState);
Vue.component('FilterPostcode', FilterPostcode);
Vue.component('FilterSequences', FilterSequences);
Vue.component('FilterSequenceStatuses', FilterSequenceStatuses);
Vue.component('FilterSources', FilterSources);
Vue.component('FilterSourcesSelector', FilterSourcesSelector);
Vue.component('FilterStatuses', FilterStatuses);
Vue.component('FilterStatusesMultiple', FilterStatusesMultiple);
Vue.component('FilterSqft', FilterSqft);
Vue.component('FilterTemperatures', FilterTemperatures);
Vue.component('FilterDealProbabilityScore', FilterDealProbabilityScore);
Vue.component('FilterTimeFrames', FilterTimeFrames);
Vue.component('FilterUsers', FilterUsers);
Vue.component('FilterYearBuilt', FilterYearBuilt);
Vue.component('FilterZipcode', FilterZipcode);

// TODO: Need to remove these after filter refactor done
Vue.component('FilterButtonClear', FilterButtonClear);
Vue.component('FilterButtonReload', FilterButtonReload);
Vue.component('FilterButtonAdd', FilterButtonAdd);
Vue.component('FilterFieldDate', FilterFieldDate);
Vue.component('FilterFieldDefault', FilterFieldDefault);
Vue.component('FilterFieldDropdown', FilterFieldDropdown);
Vue.component('FilterFieldUsers', FilterFieldUsers);
Vue.component('FilterLink', FilterLink);
// End of those to be removed, make sure you delete the files and imports as well.

// Form Components
import FormField from "./components/form/FormField";
import FormFieldAddress from "./components/form/FormFieldAddress";
import FormFieldAttachment from "./components/form/FormFieldAttachment";
import FormFieldCheckbox from "./components/form/FormFieldCheckbox";
import FormFieldCheckboxButtons from "./components/form/FormFieldCheckboxButtons";
import FormFieldCheckboxGroup from "./components/form/FormFieldCheckboxGroup";
import FormFieldCollection from "./components/form/FormFieldCollection";
import FormFieldCurrency from "./components/form/FormFieldCurrency";
import FormFieldDatePicker from "./components/form/FormFieldDatePicker";
import FormFieldDatetimePicker from "./components/form/FormFieldDatetimePicker";
import FormFieldEditor from "./components/form/FormFieldEditor";
import FormFieldFileUpload from "./components/form/FormFieldFileUpload";
import FormFieldFullName from "./components/form/FormFieldFullName";
import FormFieldInput from "./components/form/FormFieldInput";
import FormFieldInstructions from "./components/form/FormFieldInstructions";
import FormFieldPassword from "./components/form/FormFieldPassword";
import FormFieldPhone from "./components/form/FormFieldPhone";
import FormFieldRadio from "./components/form/FormFieldRadio";
import FormFieldRadioButtons from "./components/form/FormFieldRadioButtons";
import FormFieldSelect from "./components/form/FormFieldSelect";
import FormFieldSelectContactNumbers from "./components/form/FormFieldSelectContactNumbers";
import FormFieldSelectContactTypes from "./components/form/FormFieldSelectContactTypes";
import FormFieldSelectIntegrationPhoneNumber from "./components/form/FormFieldSelectIntegrationPhoneNumber";
import FormFieldSelectLeads from "./components/form/FormFieldSelectLeads";
import FormFieldSelectUserEmails from "./components/form/FormFieldSelectUserEmails";
import FormFieldSelectUsers from "./components/form/FormFieldSelectUsers";
import FormFieldSelectCampaign from "./components/form/FormFieldSelectCampaign";
import FormFieldSelectTemperature from "./components/form/FormFieldSelectTemperature";
import FormFieldSelectStatus from "./components/form/FormFieldSelectStatus";
import FormFieldSwitch from "./components/form/FormFieldSwitch";
import FormFieldTagInput from "./components/form/FormFieldTagInput";
import FormFieldTextarea from "./components/form/FormFieldTextarea";
import FormFieldTimeInMinutes from "./components/form/FormFieldTimeInMinutes";
import FormFieldTimePicker from "./components/form/FormFieldTimePicker";
import FormFieldTimezones from "./components/form/FormFieldTimezones";
import FormInput from "./components/form/FormInput";

Vue.component('FormField', FormField);
Vue.component('FormFieldAddress', FormFieldAddress);
Vue.component('FormFieldAttachment', FormFieldAttachment);
Vue.component('FormFieldCheckbox', FormFieldCheckbox);
Vue.component('FormFieldCheckboxButtons', FormFieldCheckboxButtons);
Vue.component('FormFieldCheckboxGroup', FormFieldCheckboxGroup);
Vue.component('FormFieldCollection', FormFieldCollection);
Vue.component('FormFieldCurrency', FormFieldCurrency);
Vue.component('FormFieldDatePicker', FormFieldDatePicker);
Vue.component('FormFieldDatetimePicker', FormFieldDatetimePicker);
Vue.component('FormFieldEditor', FormFieldEditor);
Vue.component('FormFieldFileUpload', FormFieldFileUpload);
Vue.component('FormFieldFullName', FormFieldFullName);
Vue.component('FormFieldInput', FormFieldInput);
Vue.component('FormFieldInstructions', FormFieldInstructions);
Vue.component('FormFieldPassword', FormFieldPassword);
Vue.component('FormFieldPhone', FormFieldPhone);
Vue.component('FormFieldRadio', FormFieldRadio);
Vue.component('FormFieldRadioButtons', FormFieldRadioButtons);
Vue.component('FormFieldSelect', FormFieldSelect);
Vue.component('FormFieldSelectContactNumbers', FormFieldSelectContactNumbers);
Vue.component('FormFieldSelectContactTypes', FormFieldSelectContactTypes);
Vue.component('FormFieldSelectIntegrationPhoneNumber', FormFieldSelectIntegrationPhoneNumber);
Vue.component('FormFieldSelectLeads', FormFieldSelectLeads);
Vue.component('FormFieldSelectUserEmails', FormFieldSelectUserEmails);
Vue.component('FormFieldSelectUsers', FormFieldSelectUsers);
Vue.component('FormFieldSelectCampaign', FormFieldSelectCampaign);
Vue.component('FormFieldSelectTemperature', FormFieldSelectTemperature);
Vue.component('FormFieldSelectStatus', FormFieldSelectStatus);
Vue.component('FormFieldSwitch', FormFieldSwitch);
Vue.component('FormFieldTagInput', FormFieldTagInput);
Vue.component('FormFieldTextarea', FormFieldTextarea);
Vue.component('FormFieldTimeInMinutes', FormFieldTimeInMinutes);
Vue.component('FormFieldTimePicker', FormFieldTimePicker);
Vue.component('FormFieldTimezones', FormFieldTimezones);
Vue.component('FormInput', FormInput);

// Grid Components
import GridColumn from "./components/grid/GridColumn";
import GridRow from "./components/grid/GridRow";

Vue.component('GridColumn', GridColumn);
Vue.component('GridRow', GridRow);

// Icon Components
import IconDefault from "./components/icons/IconDefault";

Vue.component('IconDefault', IconDefault);

// Modal Components
import ModalContainer from "./components/modal/ModalContainer";
import ModalBeforeContainer from "./components/modal/ModalBeforeContainer";
import ModalCrudForm from "./components/modal/ModalCrudForm";
import ModalDefault from "./components/modal/ModalDefault";
import ModalFormEdit from "./components/modal/ModalFormEdit";
import ModalPage from "./components/modal/ModalPage";

Vue.component('ModalContainer', ModalContainer);
Vue.component('ModalBeforeContainer', ModalBeforeContainer);
Vue.component('ModalCrudForm', ModalCrudForm);
Vue.component('ModalDefault', ModalDefault);
Vue.component('ModalFormEdit', ModalFormEdit);
Vue.component('ModalPage', ModalPage);

// Notes Components
import NotesDefault from "./components/notes/NotesDefault";
import NotesWithLatest from "./components/notes/NotesWithLatest";

Vue.component('NotesDefault', NotesDefault);
Vue.component('NotesWithLatest', NotesWithLatest);

// Record Components
import RecordAutomatedFollowUpCard from "./components/record/RecordAutomatedFollowUpCard";
import RecordIcons from "./components/record/RecordIcons";
import RecordInfo from "./components/record/RecordInfo";
import RecordNextContact from "./components/record/RecordNextContact";
import RecordPropertyDetailSummary from "./components/record/RecordPropertyDetailSummary";
import RecordStatusSelector from "./components/record/RecordStatusSelector";
import RecordTemperatureButtons from "./components/record/RecordTemperatureButtons";

Vue.component('RecordAutomatedFollowUpCard', RecordAutomatedFollowUpCard);
Vue.component('RecordIcons', RecordIcons);
Vue.component('RecordInfo', RecordInfo);
Vue.component('RecordNextContact', RecordNextContact);
Vue.component('RecordPropertyDetailSummary', RecordPropertyDetailSummary);
Vue.component('RecordStatusSelector', RecordStatusSelector);
Vue.component('RecordTemperatureButtons', RecordTemperatureButtons);

// Section Components
import SectionBasic from "./components/section/SectionBasic";
import SectionBox from "./components/section/SectionBox";
import SectionCollapseable from "./components/section/SectionCollapseable";
import SectionPage from "./components/section/SectionPage";
import SectionTable from "./components/section/SectionTable";
import SectionHeader from "./components/section/SectionHeader";

Vue.component('SectionBasic', SectionBasic);
Vue.component('SectionBox', SectionBox);
Vue.component('SectionCollapseable', SectionCollapseable);
Vue.component('SectionPage', SectionPage);
Vue.component('SectionTable', SectionTable);
Vue.component('SectionHeader', SectionHeader);

// Status Components
import StatusCheckboxGroup from "./components/status/StatusCheckboxGroup";

Vue.component('StatusCheckboxGroup', StatusCheckboxGroup);

// Tab Components
import TabsVertical from "./components/tab/TabsVertical";
import TabsHorizontal from "./components/tab/TabsHorizontal";

Vue.component('TabsHorizontal', TabsHorizontal);
Vue.component('TabsVertical', TabsVertical);

//Table Components
import TableDefault from "./components/table/TableDefault";
import TableDraggable from "./components/table/TableDraggable";

Vue.component('TableDefault', TableDefault);
Vue.component('TableDraggable', TableDraggable);

// Tag Components
import TagItem from "./components/tags/TagItem";
import TagList from "./components/tags/TagList";

Vue.component('TagItem', TagItem);
Vue.component('TagList', TagList);


// Defining array helper methods
Object.defineProperty(Array.prototype, 'first', {
  value() {
    return this.find(e => true);     // or this.find(Boolean)
  }
});

// Global Mixin
Vue.mixin({
  computed: {
    ENUMS() {
      return ENUMS;
    },
    MODELS() {
      return MODELS;
    },
  },
  methods: {
    openBillingPortal() {
      this.$buefy.toast.open({
        message: 'Opening billing portal...',
        type: 'is-info'
      });

      return api.accounts.billingPortalSession(this.$store.getters.account.id)
        .then((res) => {
          window.open(res.data)
        });
    }
  }
});

Vue.use(PiniaVuePlugin)
const pinia = createPinia()

new Vue({
  router,
  store,
  pinia,
  render: h => h(App)
}).$mount('#app');
